import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { loginUserAction } from "@/features/auth/services/authService";
import { streamService } from "@/features/chat/services/streamService";
import { changeLanguageAndPersist } from "@/i18n";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

const LANGUAGES = [
  { code: "en", label: "🇺🇸 English" },
  { code: "ja", label: "🇯🇵 日本語" },
  { code: "vi", label: "🇻🇳 Tiếng Việt" },
  { code: "id", label: "🇮🇩 Bahasa Indonesia" },
  { code: "th", label: "🇹🇭 ภาษาไทย" },
  { code: "km", label: "🇰🇭 ភាសាខ្មែរ" },
  { code: "bn", label: "🇧🇩 বাংলা" },
  { code: "my", label: "🇲🇲 မြန်မာဘာသာ" },
  { code: "ne", label: "🇳🇵 नेपाली" },
];

export const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const handleLanguageChange = async (langCode: string) => {
    setSelectedLanguage(langCode);
    await changeLanguageAndPersist(langCode);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t("auth.error_fill_fields"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = await loginUserAction(email, password);

      let streamToken = "";
      try {
        streamToken = await streamService.getToken();
      } catch (streamErr) {
        console.warn("Stream token fetch failed, continuing login:", streamErr);
      }

      if (data.user?.preferred_language) {
        await changeLanguageAndPersist(data.user.preferred_language);
        setSelectedLanguage(data.user.preferred_language);
      }

      login(data.token, streamToken, data.user, data.roles ?? []);
      navigate("/");
    } catch (err: any) {
      const raw = err.message || "";
      setError(t(raw, { defaultValue: raw }) || t("auth.error_login_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8 flex flex-col relative overflow-hidden">
      {/* 1. Updated Language Selector Position 
          Removed absolute positioning to prevent overlap, 
          using self-end to stay in the top right corner.
      */}
      <div className="flex justify-end mb-2">
        <div className="relative group">
          <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 group-hover:text-indigo-500 transition-colors">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3.654 9h16.692M3.654 15h16.692M12 3v18M12 3a12 12 0 000 18M12 3a12 12 0 010 18"
              />
            </svg>
          </div>
          <select
            value={selectedLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-8 pr-8 py-1.5 text-xs font-medium text-gray-600 hover:bg-white hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all cursor-pointer"
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-bold text-indigo-600 tracking-tight">
          {t("auth.login_title")}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t("auth.login_subtitle")}</p>
      </div>

      <form onSubmit={handleLogin} className="space-y-4">
        <Input
          label={t("auth.email_or_login_id")}
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("auth.email_or_login_id")}
        />
        <Input
          label={t("auth.password_label")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 text-xs p-3 rounded-lg animate-in fade-in slide-in-from-top-1">
            {error}
          </div>
        )}

        <Button
          type="submit"
          loading={loading}
          className="w-full py-2.5 shadow-sm shadow-indigo-200"
        >
          {t("auth.login_button")}
        </Button>
      </form>

      <div className="mt-6 md:mt-8 pt-6 border-t border-gray-50 text-center text-sm space-y-4 md:space-y-3">
        <Link
          to="/forgot-password"
          className="block py-2 text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
        >
          {t("auth.forgot_password_link")}
        </Link>
        <p className="text-gray-400">
          {t("auth.no_account")}{" "}
          <Link
            to="/register"
            className="text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            {t("auth.register_now")}
          </Link>
        </p>
        <Link
          to="/account/delete"
          className="block text-xs text-gray-400 hover:text-red-500 transition-colors"
        >
          {t("common.delete_account_link", {
            defaultValue: "アカウントを削除する",
          })}
        </Link>
        <a
          href="https://hp-chat-web.web.app/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {t("common.privacy_policy_link", {
            defaultValue: "プライバシーポリシー",
          })}
        </a>
      </div>
    </div>
  );
};
